/**
 * The document model, as the browser sees it.
 *
 * This mirrors includes/class-document.php exactly. The PHP is the authority -
 * it is what normalises anything arriving from here - and these types exist so
 * a mismatch is a compile error rather than a support ticket.
 *
 * Measurements are millimetres. Type size is points. Nothing here is in pixels;
 * pixels belong to the view and never reach the file.
 */

import type { QrFieldValues, QrKind } from '../codes/qr-types';

export const SCHEMA = 1;

export type ColourSpace = 'rgb' | 'cmyk' | 'spot';

/** 0..1 per channel, in C M Y K order. */
export type Ink = [number, number, number, number];

export type Colour =
  | { space: 'rgb'; value: string }
  | { space: 'cmyk'; value: Ink }
  | { space: 'spot'; value: string; fallback: Ink };

export interface Shadow {
  /** Offset in mm, positive being right and down. */
  x: number;
  y: number;
  colour: Colour;
  /** 0 to 1. Anything below 1 is transparency, with everything that implies. */
  opacity: number;
}

export interface Stroke {
  colour: Colour;
  width: number;
  dash: number[];
}

/** One colour along a gradient, at a position from 0 to 1. */
export interface GradientStop {
  at: number;
  colour: Colour;
}

/**
 * A gradient fill.
 *
 * Every stop is a full Colour, which means a gradient runs between real ink
 * values and reaches the press as a CMYK shading rather than as an RGB ramp
 * somebody converted afterwards. That is the same reason the rest of this
 * pipeline exists, applied to the one fill that has more than one colour in it.
 *
 * Angles are degrees clockwise from left-to-right, matching CSS, so 90 runs
 * down the page. Radial ignores the angle and works from the middle out.
 */
export interface Gradient {
  kind: 'linear' | 'radial';
  angle: number;
  stops: GradientStop[];
}

/**
 * What a shape - or a text - can be filled with.
 *
 * On a text the ramp runs across the whole text box, not letter by letter: the
 * letters are the window and the gradient is behind them, which is what a
 * customer who picks "red to blue" for a heading expects to see.
 */
export type Fill = Colour | Gradient;

/** Narrows a fill. A colour has a space; a gradient has a kind. */
export function isGradient( fill: Fill | null | undefined ): fill is Gradient {
  return !! fill && 'kind' in fill;
}

/**
 * The looks a text or a shape can take on, one at a time. §6.4.
 *
 * The same set Canva offers, because customers arrive knowing it. What each
 * one actually draws is decided in one place, effects/plan.ts, and read by the
 * canvas and by both press outputs - so a glow looks the same on screen, in the
 * proof and on the card.
 */
export type EffectKind =
  | 'drop' | 'glow' | 'echo' | 'outline' | 'background'
  | 'splice' | 'hollow' | 'neon' | 'glitch';

/**
 * An effect's settings, as the customer set them.
 *
 * Stored as the sliders' own 0-100 values rather than as millimetres, and
 * turned into millimetres against the object's size when drawn. That is what
 * makes an effect scale with its text: a shadow set on 12 pt type and the same
 * shadow on 48 pt type look like the same shadow, which is what a customer who
 * resizes a heading expects. Each kind reads the fields it has a use for and
 * ignores the rest.
 */
export interface Effect {
  kind: EffectKind;
  /**
   * The advanced preset this came from, if any. The panel then offers that
   * preset's one Intensity slider instead of the effect's own controls.
   */
  preset?: string;
  /** Degrees, -180 to 180, 0 pointing right. Drop, echo, splice, glitch. */
  direction: number;
  /** 0-100. How far the copy sits from the original. */
  offset: number;
  /** 0-100. How soft a drop shadow is. Anything above 0 is rasterised in print. */
  blur: number;
  /** 0-100. Thickness of an outline, a hollow or a splice; a glow's reach; a background's spread; neon's intensity. */
  size: number;
  /** 0-100. A background's corner rounding. */
  roundness: number;
  /** 0-100. 100 is invisible. */
  transparency: number;
  colour: Colour;
  /** Glitch's second colour. */
  colour2?: Colour;
}

/** Text laid along a drawn line, instead of in straight rows. */
export interface TextPath {
  /** The line's points, as proportions of the text layer's own box - see PathNode. */
  nodes: PathNode[];
  closed: boolean;
}

export type LayerType = 'text' | 'image' | 'shape' | 'svg' | 'graphic' | 'qr' | 'barcode' | 'group' | 'placeholder';

/**
 * Every shape the editor draws. The names are the document's, so they are
 * added to and never renamed - a saved design refers to them by these strings,
 * and an unknown one falls back to a rectangle rather than vanishing.
 *
 * What each one looks like lives in shapes/library.ts, in one definition read
 * by the canvas and by both press sinks.
 */
export type ShapeKind =
  | 'rect' | 'ellipse' | 'triangle' | 'right-triangle' | 'diamond'
  | 'parallelogram' | 'trapezium' | 'arch'
  | 'pentagon' | 'hexagon' | 'octagon' | 'polygon'
  | 'star' | 'burst' | 'seal' | 'shield' | 'tag'
  | 'line' | 'arrow' | 'double-arrow' | 'block-arrow' | 'chevron' | 'ribbon'
  | 'bubble' | 'round-callout' | 'thought'
  | 'heart' | 'blob' | 'cross' | 'frame' | 'semicircle' | 'quarter'
  // Drawn by hand. Its geometry is on the layer, in `nodes`.
  | 'free';

/** What every layer has, whatever it draws. */
export interface LayerBase {
  id: string;
  type: LayerType;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Degrees, 0..360. */
  rotation: number;
  /**
   * Mirrored, left to right or top to bottom.
   *
   * A property of the layer rather than a change to its contents, so flipping a
   * photograph does not touch the file and flipping it back is exact. Both
   * sinks mirror the layer's own frame, which means a flipped group takes
   * everything inside it with it.
   */
  flipX?: boolean;
  flipY?: boolean;
  /**
   * A drop shadow, offset from the object.
   *
   * Hard-edged, and that is a decision rather than an omission. A soft shadow
   * is a Gaussian blur, and PDF has no blur - Illustrator and every other tool
   * rasterises the layer to produce one. Rasterising here would turn crisp
   * outlined type into a bitmap at the exact moment the customer wanted it to
   * look considered, so the shadow this offers is one a press can actually
   * print: an offset copy, in ink, at whatever opacity was asked for.
   *
   * The opacity is real transparency in the press file. Ghostscript flattens it
   * when the file is conformed to PDF/X-1a; without Ghostscript the file keeps
   * it, and preflight says so.
   */
  shadow?: Shadow | null;
  opacity: number;
  locked: boolean;
  hidden: boolean;
}

/**
 * A picture seen through a text's letters.
 *
 * The same three things a picture layer has for choosing what shows - the
 * asset, the part of it (crop) and how it meets the box (fit) - measured
 * against the text's own box.
 */
export interface PictureFill {
  asset: string;
  fit: 'cover' | 'contain' | 'fill';
  crop: { x: number; y: number; w: number; h: number };
}

/**
 * A fade to transparent across a masked picture.
 *
 * Linear runs along the angle (degrees clockwise from left-to-right, as a
 * gradient does) and fades out towards the far end; radial fades out towards
 * the edges from the middle. `amount`, 0-100, is how much of the way it takes
 * to fade: 30 keeps the first 70% solid.
 */
export interface Fade {
  kind: 'linear' | 'radial';
  angle: number;
  amount: number;
}

/**
 * What a picture is seen through. §masking.
 *
 * `layer` is the outline: a shape (a frame, or a shape used as a mask), a
 * text or a drawing, kept whole so it stays editable and can be released back
 * onto the page. Its position is in the picture's own frame - measured from the
 * picture's top-left, before the picture's rotation - so moving or turning the
 * picture takes the mask with it. Null means the picture's own box, which is
 * what a soft edge or a fade on an unmasked picture is applied to.
 *
 * `frame` says the outline came with the picture as a frame from the Elements
 * panel; a frame is resized with its picture rather than released.
 *
 * `feather`, 0-100, softens the edge. A soft edge or a fade is transparency,
 * which the press file carries as a soft mask and Ghostscript flattens.
 */
export interface Mask {
  layer: ShapeLayer | TextLayer | GraphicLayer | null;
  frame: boolean;
  feather: number;
  fade: Fade | null;
}

export interface TextLayer extends LayerBase {
  type: 'text';
  text: string;
  font: { family: string; weight: number; style: 'normal' | 'italic'; size: number };
  direction: 'ltr' | 'rtl' | 'auto';
  align: 'left' | 'center' | 'right' | 'justify';
  lineHeight: number;
  letterSpacing: number;
  /**
   * Rules drawn under and through the text.
   *
   * Not font features - no font has an "underline" glyph. They are lines, and
   * in the press file they are real vector rules positioned from the font's own
   * metrics. Which also means they are the one part of a text layer that has a
   * thickness a printer can be asked about.
   */
  underline: boolean;
  strike: boolean;
  fill: Fill;
  /** A picture through the letters, instead of the fill. */
  picture?: PictureFill | null;
  stroke: Stroke | null;
  effect?: Effect | null;
  /**
   * Bent along an arc: -100 to 100, 0 straight. Positive arches upward like a
   * rainbow, negative sags like a smile. At 100 the longest line nearly closes
   * into a circle.
   */
  curve?: number;
  /** Laid along a drawn line instead. Takes precedence over the curve. */
  path?: TextPath | null;
}

export interface ImageLayer extends LayerBase {
  type: 'image';
  asset: string;
  fit: 'cover' | 'contain' | 'fill';
  crop: { x: number; y: number; w: number; h: number };
  filters: { brightness: number; contrast: number; saturation: number; greyscale: boolean };
  /** Seen through a shape, a text or a drawing, and/or with soft or faded edges. */
  mask?: Mask | null;
}

/** Whether a mask is soft: feathered or faded, and so transparency in print. */
export function isSoftMask( mask: Mask | null | undefined ): boolean {
  return !! mask && ( mask.feather > 0 || ( !! mask.fade && mask.fade.amount > 0 ) );
}

/**
 * One point on a drawn path.
 *
 * Everything is a proportion of the layer's own box, from 0 to 1, so a drawing
 * scales when the layer is resized and every handle, guide and alignment that
 * already works on a shape keeps working on this. Storing millimetres would
 * mean a path that ignored its own frame.
 *
 * The handles are absolute positions rather than offsets from the anchor. Both
 * forms are equivalent; absolute is the one that matches an SVG cubic segment,
 * which is what this eventually becomes, and it is the one that does not need
 * a sign convention explaining.
 *
 * A missing handle means a straight line into or out of the point, which is
 * how a corner is said - rather than a flag that has to be kept in step with
 * the handles it describes.
 */
export interface PathNode {
  x: number;
  y: number;
  /** Control point for the curve arriving here. */
  in?: { x: number; y: number };
  /** Control point for the curve leaving. */
  out?: { x: number; y: number };
}

export interface ShapeLayer extends LayerBase {
  type: 'shape';
  shape: ShapeKind;
  fill: Fill;
  stroke: Stroke | null;
  radius: number;
  points: number;
  /**
   * The points of a drawn path, when the shape is 'free'.
   *
   * A free-form path is a shape rather than a layer type of its own, which is
   * the whole reason it is cheap: it inherits fill, gradients, stroke, dash,
   * shadow, flipping, turning, aligning, the layer menu and the entire press
   * pipeline without any of them being told about it.
   */
  nodes?: PathNode[];
  /** Whether the last point joins back to the first. */
  closed?: boolean;
  effect?: Effect | null;
}

/**
 * A drawing from the shop's library: an icon, a border, a flourish.
 *
 * Real paths, not a picture of them. The shop's SVG is read once, when the
 * customer places it (svg/import.ts), and what lands on the card is the
 * outlines themselves - so it scales without softening, prints as vector, and
 * every colour in it is ink the customer can change.
 *
 * The paths are kept in the layer's own box, from 0 to 1, which is what makes
 * resizing it free: the box changes and the drawing follows.
 */
export interface GraphicLayer extends LayerBase {
  type: 'graphic';
  /** What it was called in the library, for the layers list. */
  source?: string;
  paths: Array<{
    /** Path data in the layer's box, 0 to 1. Only M, L, C and Z. */
    d: string;
    fill: Colour | null;
    stroke: Stroke | null;
    /** Whether the shape's holes are cut out of it, as SVG's fill-rule says. */
    evenOdd?: boolean;
  }>;
}

export interface SvgLayer extends LayerBase {
  type: 'svg';
  asset: string;
  colourMap: Record<string, Colour>;
}

export interface QrLayer extends LayerBase {
  type: 'qr';
  kind: QrKind;
  /**
   * What the code holds, as one string. For a code with `fields`, the payload
   * built from them, kept so that anything asking "is this code empty?" - the
   * server, preflight - has one plain answer.
   */
  value: string;
  /**
   * The customer's answers for this kind of code: name, phone, network and so
   * on (codes/qr-types.ts). When present these are the source of truth and the
   * payload is built from them. Codes saved before fields existed have none,
   * and are read from `value` exactly as they always were.
   */
  fields?: QrFieldValues;
  ecc: 'L' | 'M' | 'Q' | 'H';
  fill: Colour;
  background: Colour;
}

export interface BarcodeLayer extends LayerBase {
  type: 'barcode';
  symbology: 'ean13' | 'ean8' | 'code128' | 'code39' | 'upca';
  value: string;
  fill: Colour;
  showText: boolean;
}

export interface GroupLayer extends LayerBase {
  type: 'group';
  children: Layer[];
}

export interface PlaceholderLayer extends LayerBase {
  type: 'placeholder';
  placeholder: string;
  expects: 'text' | 'image';
  prompt: string;
}

export type Layer =
  | TextLayer
  | ImageLayer
  | ShapeLayer
  | GraphicLayer
  | SvgLayer
  | QrLayer
  | BarcodeLayer
  | GroupLayer
  | PlaceholderLayer;

export type PageBackground =
  | { type: 'colour'; value: Colour }
  | { type: 'image'; value: string }
  | null;

export interface Page {
  id: string;
  name: string;
  size: { w: number; h: number };
  background: PageBackground;
  layers: Layer[];
  /**
   * Two printed pages side by side, as a reader sees them.
   *
   * A booklet's inside pages are designed in spreads so a photograph can run
   * across the gutter - which is the whole reason a catalogue is not simply a
   * pile of separate pages. The page is twice as wide as the finished page;
   * the press file splits it down the middle again, each half taking its bleed
   * from the other. §6.2.
   */
  spread?: boolean;
  /**
   * The ruler guides the customer has placed on this page, in mm: `x` holds
   * vertical lines, `y` horizontal ones. Theirs, saved with the design, drawn
   * on screen only - never on the proof, in the press file or in 3D.
   */
  guides?: { x: number[]; y: number[] };
  /**
   * The outline the page is cut to, when it is not a rectangle: SVG path data in
   * page mm (a tapered mug's fan). The press file's die line and the proof's
   * edge; the designer draws it as the trim. PLAN-3D.md §7.1.
   */
  cut?: string;
  /**
   * Whether that cut is the circle inscribed in this page's box.
   *
   * `cut` is the die line and is enough to trim by, but a circle is the one
   * shape whose *inside* can still be worked out: the safe area is a smaller
   * concentric circle, and artwork near the edge can be judged against it.
   * Every other shaped page has no straight answer, so it gets no safe mark -
   * a medal insert is not one of those, and losing the safe ring on it would
   * be losing the only warning that a name is about to be cut through.
   *
   * Set by the product, never by the customer.
   */
  round?: boolean;
  /**
   * One bar depth, mm, when this page is a canvas printed to wrap round its
   * stretcher. The page is the face plus this on all four sides, and the press
   * file marks where it folds. PLAN-3D.md §7.6.
   */
  wrap?: number;
  /**
   * How the sides of a canvas are printed when they are not part of the page.
   *
   * `wrap` and this are the two halves of the same question. With an image wrap
   * the customer designs the sides, so they are in the page and `wrap` only says
   * where it folds. Mirrored or coloured, the sides are the printer's business:
   * the page stays the face, and this says what to put in the band that is added
   * round it. PLAN-3D.md §7.6.
   */
  edge?: { depth: number; mode: 'mirror' | 'colour'; colour: string };
  /**
   * How many canvases this page is printed as.
   *
   * A triptych is designed as one wide picture and printed as three. The page
   * says so; the press cuts it up (splitPanels) and the proof leaves it whole,
   * because the customer approves what they laid out.
   */
  panels?: number;
  /**
   * Parts of the sheet that are printed and then hidden by hardware - a
   * roll-up's cassette, a hem, a pole pocket.
   *
   * Shown shaded on the canvas so nothing important is laid out on them, and
   * never drawn on the press file: they describe the page rather than being
   * anything on it. PLAN-3D.md §7.2.
   */
  shade?: Array<{ x: number; y: number; w: number; h: number; name?: string }>;
  /**
   * Where the eyelets are punched, in page millimetres.
   *
   * Worked out once on the server and carried here, because an eyelet through
   * artwork is a scrapped banner and the 3D view, these guides and the press
   * file's registration marks must be the same holes rather than three answers
   * to the same sum. PLAN-3D.md §7.2.
   */
  eyelets?: Array<{ x: number; y: number }>;
}

export interface FontRef {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  subset: string;
}

export interface AssetRef {
  id: string;
  kind: 'image' | 'svg' | 'pdf';
  ref: string;
  w: number;
  h: number;
  dpi: number;
}

/**
 * How the card's corners are cut.
 *
 * Chosen from the product's own options - the shop prices rounded corners there
 * - and carried on the design so the canvas, the proof and the press file all
 * round the same corners by the same amount. The artwork is never cut to it:
 * bleed still runs square past the trim, and the cutter does the rounding.
 */
export interface Corners {
  /** The radius the cutter rounds to, in mm. 0 is square. */
  radius: number;
  /** The product options answer it came from, e.g. "rounded". */
  choice: string;
  /** That answer's words, for the proof and the order. */
  label: string;
}

export interface TrimboxDocument {
  schema: number;
  product: { id: number; variation: number };
  units: 'mm';
  /** How far artwork must run past the trim, in mm. */
  bleed: number;
  /** How far inside the trim anything important must stay, in mm. */
  safe: number;
  pages: Page[];
  fonts: FontRef[];
  assets: AssetRef[];
  variable?: { source: string; bindings: Record<string, string> };
  /** Absent on a design whose product never asked about corners. */
  corners?: Corners;
  /** Where the sheet folds, when the product is a folded piece. */
  folds?: Folds;
  /** Set when this design is a booklet: cover, inside spreads, back cover. */
  booklet?: Booklet;
}

/**
 * Where a printed sheet folds.
 *
 * Positions in millimetres from the trim's left edge (across) and top edge
 * (down), so a tri-fold whose tucked panel is a millimetre narrower - as every
 * real roll fold is - is two numbers rather than a special case. The product
 * sets them; a customer never moves a fold, because the folding machine will
 * not.
 */
export interface Folds {
  /** Vertical fold lines, mm from the left. */
  across: number[];
  /** Horizontal fold lines, mm from the top. */
  down: number[];
}

/** The folds on a page, dropping any that fall outside it. */
export function foldsOf(doc: Pick<TrimboxDocument, 'folds'>, size: { w: number; h: number }): Folds {
  const inside = (at: number, of: number) => at > 0.01 && at < of - 0.01;

  return {
    across: (doc.folds?.across ?? []).filter((at) => inside(at, size.w)),
    down: (doc.folds?.down ?? []).filter((at) => inside(at, size.h)),
  };
}

/** Whether a page has any fold across it. */
export function hasFolds(doc: Pick<TrimboxDocument, 'folds'>, size: { w: number; h: number }): boolean {
  const folds = foldsOf(doc, size);

  return folds.across.length > 0 || folds.down.length > 0;
}

/**
 * A booklet: a front cover, inside spreads, and a back cover.
 *
 * `count` is the number of finished, printed pages - what a customer orders
 * ("16 page catalogue") and what the press imposes. The document holds fewer
 * pages than that, because the insides are spreads.
 */
export interface Booklet {
  /** Printed pages in the finished booklet. Always a multiple of four. */
  count: number;
  /** The product options answer it came from, when the customer may change it. */
  choice?: string;
  label?: string;
}

/** Printed pages in a document: a spread counts as the two pages it holds. */
export function printedPages(doc: Pick<TrimboxDocument, 'pages'>): number {
  return doc.pages.reduce((total, page) => total + (page.spread ? 2 : 1), 0);
}

/** The printed page numbers a document page holds: [1] for a cover, [2, 3] for a spread. */
export function pageNumbers(doc: Pick<TrimboxDocument, 'pages'>, index: number): number[] {
  let at = 1;

  for (let i = 0; i < index; i++) at += doc.pages[i]?.spread ? 2 : 1;

  return doc.pages[index]?.spread ? [at, at + 1] : [at];
}

/**
 * What a page is called on its title.
 *
 * A booklet's pages are named by where they fall - a customer thinks "the
 * cover" and "pages 8 and 9", not "page 5 of the file" - and anything else
 * keeps the name the product gave it.
 */
export function pageLabel(doc: Pick<TrimboxDocument, 'pages' | 'booklet'>, index: number): string {
  const page = doc.pages[index];

  if (!page) return '';

  if (!doc.booklet) return page.name;

  const numbers = pageNumbers(doc, index);
  const last = printedPages(doc);

  if (numbers[0] === 1) return 'Front cover';
  if (numbers[numbers.length - 1] === last) return 'Back cover';

  return numbers.length > 1 ? `Pages ${numbers[0]}–${numbers[1]}` : `Page ${numbers[0]}`;
}

/** A page's ruler guides, always as two lists. */
export function guidesOf(page: Pick<TrimboxDocument['pages'][number], 'guides'> | null | undefined): { x: number[]; y: number[] } {
  return { x: page?.guides?.x ?? [], y: page?.guides?.y ?? [] };
}

/** The corner radius to draw, in mm: 0 for square or when nobody asked. */
export function cornerRadius(doc: Pick<TrimboxDocument, 'corners'>, size?: { w: number; h: number }): number {
  const radius = doc.corners?.radius ?? 0;

  if (!(radius > 0)) return 0;

  // Never more than half the short side - past that it is not a corner.
  return size ? Math.min(radius, size.w / 2, size.h / 2) : radius;
}

/** What the REST routes hand back around a document. */
export interface Design {
  uuid: string;
  title: string;
  status: 'draft' | 'saved' | 'ordered';
  product: number;
  variation: number;
  orderItem: number;
  thumb: string;
  /** The design on the product in 3D, when the product shows 3D. */
  thumb3d?: string;
  created: string;
  updated: string;
  document: TrimboxDocument;
}

/* ---------------------------------------------------------------
 * Narrowing, so the renderer never has to cast
 * ------------------------------------------------------------ */

export const isText = (l: Layer): l is TextLayer => l.type === 'text';
export const isImage = (l: Layer): l is ImageLayer => l.type === 'image';
export const isShape = (l: Layer): l is ShapeLayer => l.type === 'shape';
export const isGroup = (l: Layer): l is GroupLayer => l.type === 'group';
export const isPlaceholder = (l: Layer): l is PlaceholderLayer => l.type === 'placeholder';

/** Every layer on a page, groups flattened, in drawing order. */
export function flatten(layers: Layer[]): Layer[] {
  const out: Layer[] = [];

  for (const layer of layers) {
    out.push(layer);

    if (isGroup(layer)) out.push(...flatten(layer.children));
  }

  return out;
}

/** Finds one layer anywhere in a page, including inside a group. */
export function findLayer(layers: Layer[], id: string): Layer | null {
  for (const layer of layers) {
    if (layer.id === id) return layer;

    if (isGroup(layer)) {
      const found = findLayer(layer.children, id);

      if (found) return found;
    }
  }

  return null;
}
